import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Car, Download, ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import AdminDialog from "@/components/admin/AdminDialog";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/utils/apiError";
import { formatDateTimeMdY } from "@/utils/dateFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const RESULTS_PAGE_SIZES = [25, 35, 50];
const DEFAULT_RESULTS_PAGE_SIZE = 25;

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white px-3 py-2.5 font-['Manrope'] text-base focus:outline-none focus:border-blue-500/50";
const selectCls = `${inputCls} [&>option]:bg-gray-900 [&>option]:text-white`;
const labelCls = "block text-xs font-['Manrope'] text-white/50 mb-1";
const mediaThumbCls = "relative w-28 h-28 sm:w-32 sm:h-32 overflow-hidden border";
const mediaTileCls =
  "w-28 h-28 sm:w-32 sm:h-32 border text-white/70 hover:border-blue-400 hover:text-white flex flex-col items-center justify-center gap-1 disabled:opacity-50";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function statusLabel(status) {
  const text = String(status || "").replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

function isTimeoutJob(job) {
  if (!job) return false;
  if (job.timeout_recoverable && job.status === "failed") return true;
  return job.status === "failed" && /timed out waiting for higgsfield/i.test(job.error || "");
}

function isVideoMedia(item) {
  const type = item?.resource_type || item?.type || "";
  if (type === "video") return true;
  const url = String(item?.url || "").split("?")[0].toLowerCase();
  return url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".mov") || url.endsWith(".m4v");
}

function mediaItems(job) {
  const clouds = Array.isArray(job?.cloudinary_assets) ? job.cloudinary_assets : [];
  if (clouds.some((item) => item?.url)) {
    return clouds.filter((item) => item?.url).map((item) => ({
      url: item.url,
      resource_type: item.resource_type || "image",
      thumbnail_url: item.thumbnail_url,
    }));
  }
  const raw = Array.isArray(job?.result_urls) ? job.result_urls : [];
  return raw.filter((item) => item?.url).map((item) => ({
    url: item.url,
    resource_type: item.type || item.resource_type || "image",
    thumbnail_url: item.thumbnail_url,
  }));
}

function isWorkingJob(job) {
  if (!job) return false;
  if (job.status === "queued" || job.status === "in_progress" || isTimeoutJob(job)) return true;
  return job.status === "completed" && mediaItems(job).length === 0;
}

function workingLabel(job) {
  return job?.kind === "generate_video" ? "Generating video…" : "Generating photo…";
}

function jobKindLabel(job) {
  if (job?.kind === "generate_video") return "Video";
  if (job?.kind === "generate_photo") return "Photo";
  return job?.kind || "Imagine";
}

function modelLimit(model, key) {
  const value = Number(model?.[key]);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function modelFields(model) {
  return Array.isArray(model?.fields) ? model.fields : [];
}

function referenceLimitHint(model, mode) {
  if (!model) return "Upload references or pick them from a vehicle.";
  const maxImages = modelLimit(model, "max_images");
  const minImages = modelLimit(model, "min_images");
  const maxVideos = modelLimit(model, "max_videos");
  const minVideos = modelLimit(model, "min_videos");
  const parts = [];
  if (maxImages <= 0) {
    parts.push("This model does not use reference photos.");
  } else if (minImages > 0 && minImages === maxImages) {
    parts.push(`Use ${maxImages} reference photo${maxImages === 1 ? "" : "s"}.`);
  } else if (minImages > 0) {
    parts.push(`Use ${minImages}–${maxImages} reference photos.`);
  } else {
    parts.push(`Up to ${maxImages} reference photo${maxImages === 1 ? "" : "s"} (optional).`);
  }
  if (mode === "video" && maxVideos > 0) {
    if (minVideos > 0 && minVideos === maxVideos) {
      parts.push(`Use ${maxVideos} reference video${maxVideos === 1 ? "" : "s"}.`);
    } else if (minVideos > 0) {
      parts.push(`Use ${minVideos}–${maxVideos} reference videos.`);
    } else {
      parts.push(`Up to ${maxVideos} reference video${maxVideos === 1 ? "" : "s"} (optional).`);
    }
  }
  return parts.join(" ");
}

function formatJobCost(job) {
  const estimate = job?.estimate && typeof job.estimate === "object" ? job.estimate : {};
  const usd = estimate.usd;
  const credits = estimate.credits;
  const usdNum = usd == null || usd === "" ? NaN : Number(usd);
  const parts = [];
  if (!Number.isNaN(usdNum)) parts.push(`$${usdNum.toFixed(2)}`);
  else if (usd != null && usd !== "") parts.push(`$${usd}`);
  if (credits != null && credits !== "") parts.push(`${credits} credits`);
  return parts.length ? parts.join(" · ") : "Unavailable";
}

function jobGeneratedParts(job) {
  const name = (job?.admin_name || "").trim();
  const role = (job?.admin_role_label || "").trim();
  const email = (job?.admin_email || "").trim();
  const combined = (job?.generated_by || "").trim();
  const personIsRole = Boolean(name && role && name.toLowerCase() === role.toLowerCase());
  let person = personIsRole ? email : name;
  if (!person && combined.includes(" · ")) person = combined.split(" · ")[0].trim();
  person = person || email || combined || "Unknown";
  let position = role;
  if (!position && combined.includes(" · ")) {
    position = combined.split(" · ").slice(1).join(" · ").trim();
  }
  if (position && person.toLowerCase() === position.toLowerCase()) position = "";
  return { name: person, role: position };
}

function mediaExtension(item) {
  if (isVideoMedia(item)) {
    const path = String(item?.url || "").split("?")[0].toLowerCase();
    if (path.endsWith(".webm")) return "webm";
    if (path.endsWith(".mov")) return "mov";
    if (path.endsWith(".m4v")) return "m4v";
    return "mp4";
  }
  const path = String(item?.url || "").split("?")[0].toLowerCase();
  const ext = path.split(".").pop();
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return ext === "jpeg" ? "jpg" : ext;
  return "jpg";
}

function downloadFilename(job, item, index, total) {
  const ext = mediaExtension(item);
  const kind = job?.kind === "generate_video" || isVideoMedia(item) ? "video" : "photo";
  const suffix = total > 1 ? `-${index + 1}` : "";
  const id = String(job?.id || "file").slice(0, 8);
  return `drivenow-imagine-${kind}-${id}${suffix}.${ext}`;
}

function mediaAttachmentUrl(url, filename) {
  const text = String(url || "");
  if (text.includes("res.cloudinary.com/") && text.includes("/upload/")) {
    const safe = encodeURIComponent(String(filename || "download").replace(/[^\w.\-]+/g, "_"));
    return text.replace("/upload/", `/upload/fl_attachment:${safe}/`);
  }
  return text;
}

async function downloadUrl(url, filename) {
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error("fetch failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    const link = document.createElement("a");
    link.href = mediaAttachmentUrl(url, filename);
    link.download = filename;
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  }
}

function jobReferencePreviewUrls(job) {
  const seen = new Set();
  const urls = [];
  const add = (url) => {
    const text = String(url || "").trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    urls.push(text);
  };
  (Array.isArray(job?.reference_image_urls) ? job.reference_image_urls : []).forEach(add);
  add(job?.first_frame_url);
  add(job?.last_frame_url);
  return urls;
}

function vehicleTitle(vehicle) {
  if (!vehicle) return "";
  return [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ");
}

function resolutionChoices(model) {
  if (!modelFields(model).includes("resolution")) return [];
  const fallback = model?.output === "video" ? ["480p", "720p", "1080p"] : ["1k", "2k"];
  const preferred = model?.defaults?.resolution;
  if (preferred && !fallback.includes(preferred)) return [preferred, ...fallback];
  return fallback;
}

function aspectChoices(model) {
  if (!modelFields(model).includes("aspect_ratio")) return [];
  const base = ["16:9", "9:16", "1:1", "4:3", "3:4", "auto"];
  const preferred = model?.defaults?.aspect_ratio;
  if (preferred && !base.includes(preferred)) return [preferred, ...base];
  return base;
}

function qualityChoices(model) {
  if (!modelFields(model).includes("quality")) return [];
  const base = ["medium", "high"];
  const preferred = model?.defaults?.quality;
  if (preferred && !base.includes(preferred)) return [preferred, ...base];
  return base;
}

function dedupeMedia(rows) {
  const seen = new Set();
  const out = [];
  rows.forEach((item) => {
    const url = item?.url;
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push(item);
  });
  return out;
}

function RemoveThumbButton({ label, onClick }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="absolute top-0 right-0 z-10 w-11 h-11 flex items-center justify-center bg-black/75 text-white hover:bg-black"
    >
      <X className="w-4 h-4" aria-hidden="true" />
    </button>
  );
}

export default function AdminImaginePage() {
  const { token } = useAuth();
  const [models, setModels] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [mode, setMode] = useState("photo");
  const [genModel, setGenModel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [uploads, setUploads] = useState([]);
  const [vehiclePicks, setVehiclePicks] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [firstFrameUrl, setFirstFrameUrl] = useState("");
  const [lastFrameUrl, setLastFrameUrl] = useState("");
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState("");
  const [aspectRatio, setAspectRatio] = useState("");
  const [quality, setQuality] = useState("");
  const [generateAudio, setGenerateAudio] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState("");
  const [applyingId, setApplyingId] = useState("");
  const [infoJob, setInfoJob] = useState(null);
  const [resultsPageSize, setResultsPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const [resultsPage, setResultsPage] = useState(1);
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [pickerVehicle, setPickerVehicle] = useState(null);
  const [pickerImages, setPickerImages] = useState([]);
  const [pickerVideos, setPickerVideos] = useState([]);
  const uploadRef = useRef(null);

  const photoModels = useMemo(() => models.filter((model) => model.kind === "photo"), [models]);
  const videoModels = useMemo(() => models.filter((model) => model.kind === "video"), [models]);
  const genModels = mode === "video" ? videoModels : photoModels;
  const activeGen = genModels.find((model) => model.id === genModel) || genModels[0];
  const fields = modelFields(activeGen);
  const supportsFrames = mode === "video" && Boolean(activeGen?.supports_first_last_frame);
  const maxImages = modelLimit(activeGen, "max_images");
  const maxVideos = modelLimit(activeGen, "max_videos");
  const allowPhotos = maxImages > 0;
  const allowVideos = mode === "video" && maxVideos > 0;
  const wantsAudio = fields.includes("generate_audio") || fields.includes("sound");

  const uploadPhotos = useMemo(
    () => uploads.filter((item) => !isVideoMedia(item)),
    [uploads],
  );
  const uploadVideos = useMemo(
    () => uploads.filter((item) => isVideoMedia(item)),
    [uploads],
  );
  const pickedPhotos = useMemo(
    () => vehiclePicks.filter((item) => !isVideoMedia(item)),
    [vehiclePicks],
  );
  const pickedVideos = useMemo(
    () => vehiclePicks.filter((item) => isVideoMedia(item)),
    [vehiclePicks],
  );
  const chosenImages = useMemo(() => {
    const urls = [...selectedImages];
    pickedPhotos.forEach((item) => {
      if (item.url && !urls.includes(item.url)) urls.push(item.url);
    });
    return urls;
  }, [selectedImages, pickedPhotos]);
  const chosenVideos = useMemo(() => {
    if (!allowVideos) return [];
    const urls = [...selectedVideos];
    pickedVideos.forEach((item) => {
      if (item.url && !urls.includes(item.url)) urls.push(item.url);
    });
    return urls;
  }, [allowVideos, selectedVideos, pickedVideos]);
  const framePhotos = useMemo(() => dedupeMedia([
    ...uploadPhotos.filter((item) => selectedImages.includes(item.url)),
    ...pickedPhotos,
  ]), [uploadPhotos, selectedImages, pickedPhotos]);
  const pickedVehicleLabels = useMemo(() => {
    const labels = [];
    vehiclePicks.forEach((item) => {
      const label = item.vehicleLabel || "Vehicle";
      if (!labels.includes(label)) labels.push(label);
    });
    return labels;
  }, [vehiclePicks]);

  const loadModels = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/imagine/models`, {
        headers: authHeaders(token),
      });
      setConfigured(Boolean(data.configured));
      setModels(data.models || []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load Imagine models"));
    }
  }, [token]);

  const loadJobs = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/imagine/jobs`, {
        headers: authHeaders(token),
      });
      setJobs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load Imagine jobs"));
    }
  }, [token]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (!genModels.length) return;
    if (!genModels.some((model) => model.id === genModel)) setGenModel(genModels[0].id);
  }, [genModels, genModel]);

  useEffect(() => {
    if (!activeGen?.id) return;
    const defaults = activeGen.defaults || {};
    setDuration(defaults.duration ?? 5);
    setResolution(defaults.resolution || "");
    setAspectRatio(defaults.aspect_ratio || "");
    setQuality(defaults.quality || "");
    setGenerateAudio(defaults.generate_audio !== false && defaults.sound !== "off");
    setFirstFrameUrl("");
    setLastFrameUrl("");
  }, [activeGen?.id]);

  useEffect(() => {
    const keptPicks = [];
    let images = 0;
    let videos = 0;
    vehiclePicks.forEach((item) => {
      if (isVideoMedia(item)) {
        if (!allowVideos) {
          keptPicks.push(item);
          return;
        }
        if (videos >= maxVideos) return;
        videos += 1;
        keptPicks.push(item);
        return;
      }
      if (maxImages <= 0 || images >= maxImages) return;
      images += 1;
      keptPicks.push(item);
    });
    const imageSlots = Math.max(0, maxImages - images);
    const videoSlots = Math.max(0, (allowVideos ? maxVideos : 0) - videos);
    const nextImages = maxImages > 0 ? selectedImages.slice(0, imageSlots) : [];
    const nextVideos = allowVideos ? selectedVideos.slice(0, videoSlots) : selectedVideos;
    const samePicks = keptPicks.length === vehiclePicks.length
      && keptPicks.every((item, index) => item.url === vehiclePicks[index]?.url);
    if (!samePicks) setVehiclePicks(keptPicks);
    if (nextImages.length !== selectedImages.length) setSelectedImages(nextImages);
    if (allowVideos && nextVideos.length !== selectedVideos.length) setSelectedVideos(nextVideos);
    // Trim only when the model cap changes. Photo mode keeps stored video picks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGen?.id, maxImages, maxVideos, allowVideos]);

  useEffect(() => {
    const urls = new Set(framePhotos.map((item) => item.url));
    if (firstFrameUrl && !urls.has(firstFrameUrl)) setFirstFrameUrl("");
    if (lastFrameUrl && !urls.has(lastFrameUrl)) setLastFrameUrl("");
  }, [framePhotos, firstFrameUrl, lastFrameUrl]);

  const pending = jobs.some(isWorkingJob);
  useEffect(() => {
    if (!pending) return undefined;
    const tick = () => {
      if (!document.hidden) loadJobs();
    };
    const id = setInterval(tick, 3000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [pending, loadJobs]);

  useEffect(() => {
    const total = Math.max(1, Math.ceil(jobs.length / resultsPageSize));
    if (resultsPage > total) setResultsPage(total);
  }, [jobs.length, resultsPage, resultsPageSize]);

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || !token) return;
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    setUploading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/imagine/upload`, formData, {
        headers: authHeaders(token),
      });
      const added = Array.isArray(data?.uploads) ? data.uploads : [];
      if (!added.length) {
        toast.error("No files were uploaded");
        return;
      }
      const pickedPhotoCount = vehiclePicks.filter((item) => !isVideoMedia(item)).length;
      const pickedVideoCount = vehiclePicks.filter((item) => isVideoMedia(item)).length;
      const uploadImageRoom = Math.max(0, maxImages - pickedPhotoCount);
      const uploadVideoRoom = allowVideos ? Math.max(0, maxVideos - pickedVideoCount) : 0;
      setUploads((cur) => {
        const seen = new Set(cur.map((item) => item.url));
        const next = [...cur];
        added.forEach((item) => {
          if (item?.url && !seen.has(item.url)) {
            seen.add(item.url);
            next.push(item);
          }
        });
        return next;
      });
      setSelectedImages((cur) => {
        if (uploadImageRoom <= 0) return cur;
        const next = [...cur];
        added.forEach((item) => {
          if (!item?.url || isVideoMedia(item) || next.includes(item.url) || next.length >= uploadImageRoom) return;
          next.push(item.url);
        });
        return next;
      });
      setSelectedVideos((cur) => {
        if (uploadVideoRoom <= 0) return cur;
        const next = [...cur];
        added.forEach((item) => {
          if (!item?.url || !isVideoMedia(item) || next.includes(item.url) || next.length >= uploadVideoRoom) return;
          next.push(item.url);
        });
        return next;
      });
      const videoCount = added.filter((item) => isVideoMedia(item)).length;
      if (videoCount && mode !== "video") {
        toast.success("Video uploaded. Switch to Video to use it as a reference.");
      } else {
        toast.success(added.length === 1 ? "Reference uploaded" : `${added.length} references uploaded`);
      }
    } catch (error) {
      toast.error(formatApiError(error, "Could not upload references"));
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = "";
    }
  };

  const loadVehicles = useCallback(async () => {
    if (!token) return;
    setVehiclesLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/imagine/vehicles`, {
        headers: authHeaders(token),
      });
      setVehicles(Array.isArray(data?.vehicles) ? data.vehicles : []);
    } catch (error) {
      toast.error(formatApiError(error, "Could not load vehicles"));
    } finally {
      setVehiclesLoading(false);
    }
  }, [token]);

  const openVehiclePicker = () => {
    setPickerVehicle(null);
    setPickerImages([]);
    setPickerVideos([]);
    setVehicleQuery("");
    setVehicleOpen(true);
    if (!vehicles.length) loadVehicles();
  };

  const openVehicleMedia = (row) => {
    const fromVehicle = vehiclePicks.filter((item) => item.vehicleId === row.id);
    setPickerVehicle(row);
    setPickerImages(fromVehicle.filter((item) => !isVideoMedia(item)).map((item) => item.url));
    setPickerVideos(fromVehicle.filter((item) => isVideoMedia(item)).map((item) => item.url));
  };

  const referenceRoom = (kind, exceptVehicleId) => {
    const photos = kind === "photo";
    const cap = photos ? maxImages : maxVideos;
    if (cap <= 0 || (!photos && !allowVideos)) return 0;
    const picks = (photos ? pickedPhotos : pickedVideos).filter(
      (item) => item.vehicleId !== exceptVehicleId,
    ).length;
    const selectedUploads = photos ? selectedImages.length : selectedVideos.length;
    return Math.max(0, cap - picks - selectedUploads);
  };

  const confirmVehicleMedia = () => {
    if (!pickerVehicle) return;
    const id = pickerVehicle.id;
    const had = vehiclePicks.some((item) => item.vehicleId === id);
    const photoByUrl = new Map((pickerVehicle.photos || []).map((item) => [item.url, item]));
    const videoByUrl = new Map((pickerVehicle.videos || []).map((item) => [item.url, item]));
    const wantedPhotos = (allowPhotos ? pickerImages : []).filter((url) => photoByUrl.has(url));
    const wantedVideos = (allowVideos ? pickerVideos : []).filter((url) => videoByUrl.has(url));
    if (!wantedPhotos.length && !wantedVideos.length && !had) {
      toast.error(allowVideos ? "Select at least one photo or video" : "Select at least one photo");
      return;
    }
    const keptPhotos = wantedPhotos.slice(0, referenceRoom("photo", id));
    const keptVideos = wantedVideos.slice(0, referenceRoom("video", id));
    if ((wantedPhotos.length || wantedVideos.length) && !keptPhotos.length && !keptVideos.length) {
      toast.error("This model is already using the maximum number of references");
      return;
    }
    const label = vehicleTitle(pickerVehicle);
    const stockNumber = pickerVehicle.stock_number || "";
    const added = [];
    const pushPick = (url, source, resourceType) => {
      if (!source || added.some((item) => item.url === url)) return;
      added.push({
        url,
        thumbnail_url: source.thumbnail_url || null,
        resource_type: resourceType,
        vehicleId: id,
        vehicleLabel: label,
        stockNumber,
      });
    };
    keptPhotos.forEach((url) => pushPick(url, photoByUrl.get(url), "image"));
    keptVideos.forEach((url) => pushPick(url, videoByUrl.get(url), "video"));
    setVehiclePicks((cur) => {
      const others = cur.filter((item) => item.vehicleId !== id);
      const keepHidden = cur.filter((item) => {
        if (item.vehicleId !== id) return false;
        return isVideoMedia(item) ? !allowVideos : !allowPhotos;
      });
      return [...others, ...keepHidden, ...added];
    });
    const trimmed = keptPhotos.length < wantedPhotos.length || keptVideos.length < wantedVideos.length;
    if (trimmed) {
      toast.error("Some checked photos did not fit this model's reference limit");
    }
    setVehicleOpen(false);
  };

  const dropReferenceUrl = (url) => {
    if (firstFrameUrl === url) setFirstFrameUrl("");
    if (lastFrameUrl === url) setLastFrameUrl("");
  };

  const removeUpload = (url) => {
    setUploads((cur) => cur.filter((item) => item.url !== url));
    setSelectedImages((cur) => cur.filter((item) => item !== url));
    setSelectedVideos((cur) => cur.filter((item) => item !== url));
    dropReferenceUrl(url);
  };

  const removeAllUploads = () => {
    const urls = new Set(uploads.map((item) => item.url));
    setUploads([]);
    setSelectedImages((cur) => cur.filter((url) => !urls.has(url)));
    setSelectedVideos((cur) => cur.filter((url) => !urls.has(url)));
    if (urls.has(firstFrameUrl)) setFirstFrameUrl("");
    if (urls.has(lastFrameUrl)) setLastFrameUrl("");
  };

  const removeVehiclePick = (url) => {
    setVehiclePicks((cur) => cur.filter((item) => item.url !== url));
    dropReferenceUrl(url);
  };

  const toggleUploadPhoto = (url) => {
    if (maxImages <= 0) {
      toast.error("This model does not use reference photos");
      return;
    }
    if (selectedImages.includes(url)) {
      setSelectedImages(selectedImages.filter((item) => item !== url));
      return;
    }
    if (maxImages === 1 && pickedPhotos.length === 0) {
      setSelectedImages([url]);
      return;
    }
    if (selectedImages.length >= referenceRoom("photo")) {
      toast.error(`This model allows up to ${maxImages} reference photo${maxImages === 1 ? "" : "s"}`);
      return;
    }
    setSelectedImages([...selectedImages, url]);
  };

  const toggleUploadVideo = (url) => {
    if (!allowVideos) {
      toast.error(mode === "video"
        ? "This model does not use reference videos"
        : "Switch to Video to use this reference");
      return;
    }
    if (selectedVideos.includes(url)) {
      setSelectedVideos(selectedVideos.filter((item) => item !== url));
      return;
    }
    if (maxVideos === 1 && pickedVideos.length === 0) {
      setSelectedVideos([url]);
      return;
    }
    if (selectedVideos.length >= referenceRoom("video")) {
      toast.error(`This model allows up to ${maxVideos} reference video${maxVideos === 1 ? "" : "s"}`);
      return;
    }
    setSelectedVideos([...selectedVideos, url]);
  };

  const togglePickerPhoto = (url) => {
    if (maxImages <= 0) {
      toast.error("This model does not use reference photos");
      return;
    }
    if (pickerImages.includes(url)) {
      setPickerImages(pickerImages.filter((item) => item !== url));
      return;
    }
    const room = referenceRoom("photo", pickerVehicle?.id);
    if (maxImages === 1 && room > 0) {
      setPickerImages([url]);
      return;
    }
    if (pickerImages.length >= room) {
      toast.error(`This model allows up to ${maxImages} reference photo${maxImages === 1 ? "" : "s"}`);
      return;
    }
    setPickerImages([...pickerImages, url]);
  };

  const togglePickerVideo = (url) => {
    if (!allowVideos) {
      toast.error("This model does not use reference videos");
      return;
    }
    if (pickerVideos.includes(url)) {
      setPickerVideos(pickerVideos.filter((item) => item !== url));
      return;
    }
    const room = referenceRoom("video", pickerVehicle?.id);
    if (maxVideos === 1 && room > 0) {
      setPickerVideos([url]);
      return;
    }
    if (pickerVideos.length >= room) {
      toast.error(`This model allows up to ${maxVideos} reference video${maxVideos === 1 ? "" : "s"}`);
      return;
    }
    setPickerVideos([...pickerVideos, url]);
  };

  const submitGenerate = async () => {
    const neededPhotos = Number(activeGen?.min_images) || 0;
    const neededVideos = Number(activeGen?.min_videos) || 0;
    const photoCount = chosenImages.length + (
      firstFrameUrl && !chosenImages.includes(firstFrameUrl) ? 1 : 0
    ) + (
      lastFrameUrl && !chosenImages.includes(lastFrameUrl) && lastFrameUrl !== firstFrameUrl ? 1 : 0
    );
    if (activeGen?.requires_prompt && !prompt.trim()) {
      toast.error("Enter a prompt for this model");
      return;
    }
    if (neededPhotos && photoCount < neededPhotos) {
      toast.error(`This model needs at least ${neededPhotos} reference photo${neededPhotos === 1 ? "" : "s"}`);
      return;
    }
    if (neededVideos && chosenVideos.length < neededVideos) {
      toast.error(`This model needs at least ${neededVideos} reference video${neededVideos === 1 ? "" : "s"}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/imagine/jobs`,
        {
          kind: mode === "video" ? "generate_video" : "generate_photo",
          model_id: activeGen?.id,
          prompt: prompt.trim(),
          image_urls: chosenImages,
          video_urls: chosenVideos,
          vehicle_ids: [...new Set(vehiclePicks.map((item) => item.vehicleId).filter(Boolean))],
          duration: fields.includes("duration") ? Number(duration) || 5 : undefined,
          resolution: resolution || undefined,
          aspect_ratio: aspectRatio || undefined,
          quality: quality || undefined,
          generate_audio: wantsAudio ? Boolean(generateAudio) : undefined,
          first_frame_url: supportsFrames ? firstFrameUrl || undefined : undefined,
          last_frame_url: supportsFrames ? lastFrameUrl || undefined : undefined,
        },
        { headers: authHeaders(token) },
      );
      toast.success("Imagine job submitted");
      if (data?.id) {
        setJobs((cur) => [data, ...cur.filter((job) => job.id !== data.id)]);
      }
      setResultsPage(1);
      await loadJobs();
    } catch (error) {
      toast.error(formatApiError(error, "Could not start generation"));
    } finally {
      setBusy(false);
    }
  };

  const downloadJob = async (job) => {
    const items = mediaItems(job).filter((item) => item?.url);
    if (!items.length) {
      toast.error("No generated media to download yet");
      return;
    }
    setDownloadingId(job.id);
    try {
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index];
        await downloadUrl(item.url, downloadFilename(job, item, index, items.length));
      }
      toast.success(items.length === 1 ? "Download started" : `${items.length} files downloading`);
    } catch {
      toast.error("Could not download generated media");
    } finally {
      setDownloadingId("");
    }
  };

  const applyJob = async (job) => {
    const action = job.in_media ? "remove" : "add";
    setApplyingId(job.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/imagine/jobs/${job.id}/apply`,
        { action },
        { headers: authHeaders(token) },
      );
      toast.success(action === "remove" ? "Removed from vehicle media" : "Added to vehicle media");
      await loadJobs();
    } catch (error) {
      toast.error(formatApiError(error, "Could not update vehicle media"));
    } finally {
      setApplyingId("");
    }
  };

  const filteredVehicles = useMemo(() => {
    const query = vehicleQuery.trim().toLowerCase();
    if (!query) return vehicles;
    return vehicles.filter((row) => {
      const hay = `${row.year || ""} ${row.make || ""} ${row.model || ""} ${row.trim || ""} ${row.stock_number || ""}`.toLowerCase();
      return hay.includes(query);
    });
  }, [vehicles, vehicleQuery]);

  const generateWorking = jobs.some(
    (job) => isWorkingJob(job) && (job.kind === "generate_video" || job.kind === "generate_photo"),
  );
  const resultsTotalPages = Math.max(1, Math.ceil(jobs.length / resultsPageSize));
  const resultsSafePage = Math.min(resultsPage, resultsTotalPages);
  const pagedJobs = jobs.slice(
    (resultsSafePage - 1) * resultsPageSize,
    resultsSafePage * resultsPageSize,
  );
  const infoRefs = infoJob ? jobReferencePreviewUrls(infoJob) : [];
  const infoGenerated = infoJob ? jobGeneratedParts(infoJob) : { name: "", role: "" };
  const generateLabel = busy ? "Submitting…" : generateWorking ? "Generating…" : mode === "video" ? "Generate video" : "Generate photo";

  const generateButton = (testId, className) => (
    <button
      type="button"
      data-testid={testId}
      disabled={busy || !configured || !activeGen}
      onClick={submitGenerate}
      className={`min-h-11 px-4 inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50 ${className}`}
    >
      {(busy || generateWorking) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
      {generateLabel}
    </button>
  );

  return (
    <div className="p-4 sm:p-6 pb-28 lg:pb-6" data-testid="admin-imagine-page">
      <AdminPageHeader
        title="Imagine"
        subtitle="Marketing photos and videos. Upload references, or combine photos and videos from vehicles on the lot."
        testId="admin-imagine-header"
      />

      {!configured && (
        <p className="text-sm font-['Manrope'] text-amber-300 mb-4" role="alert">
          Higgsfield keys are missing. Set HF_API_KEY_ID and HF_API_KEY_SECRET on the backend.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)] gap-6 items-start">
        <section className="bg-white/5 border border-white/10 p-4 sm:p-5 lg:sticky lg:top-4 lg:max-h-[calc(100dvh-2rem)] lg:overflow-y-auto space-y-4">
          <div className="grid grid-cols-2 gap-2" role="tablist" aria-label="Output">
            {["photo", "video"].map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                data-testid={`imagine-mode-${value}`}
                onClick={() => setMode(value)}
                className={`min-h-11 font-['Manrope'] font-semibold capitalize ${
                  mode === value ? "bg-blue-500 text-white" : "bg-white/5 border border-white/10 text-white/70"
                }`}
              >
                {value}
              </button>
            ))}
          </div>

          <div>
            <label className={labelCls} htmlFor="imagine-model">Model</label>
            <select
              id="imagine-model"
              className={selectCls}
              value={activeGen?.id || ""}
              onChange={(event) => setGenModel(event.target.value)}
            >
              {genModels.map((model) => (
                <option key={model.id} value={model.id}>{model.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls} htmlFor="imagine-prompt">Prompt</label>
            <textarea
              id="imagine-prompt"
              data-testid="imagine-prompt"
              className={`${inputCls} min-h-[120px]`}
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="Describe the photo or video you want for a listing, ad, or social post"
            />
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className={labelCls}>References</span>
              {uploads.length > 0 && (
                <button
                  type="button"
                  data-testid="imagine-remove-uploads"
                  onClick={removeAllUploads}
                  className="text-xs font-['Manrope'] text-white/50 hover:text-white min-h-11"
                >
                  Remove uploads
                </button>
              )}
            </div>
            {pickedVehicleLabels.length > 0 && (
              <p className="mb-2 text-sm font-['Manrope'] text-white/80">
                From {pickedVehicleLabels.join(", ")}
              </p>
            )}
            <div className="flex flex-wrap gap-3">
              {uploadPhotos.map((photo) => {
                const on = selectedImages.includes(photo.url);
                return (
                  <div
                    key={photo.url}
                    className={`${mediaThumbCls} ${on ? "border-blue-400" : "border-white/15"}`}
                  >
                    <button
                      type="button"
                      aria-pressed={on}
                      aria-label="Toggle uploaded photo"
                      onClick={() => toggleUploadPhoto(photo.url)}
                      className="w-full h-full"
                    >
                      <img src={photo.thumbnail_url || photo.url} alt="" className="w-full h-full object-cover" />
                    </button>
                    <RemoveThumbButton label="Remove uploaded photo" onClick={() => removeUpload(photo.url)} />
                  </div>
                );
              })}
              {uploadVideos.map((video) => {
                const on = allowVideos && selectedVideos.includes(video.url);
                return (
                  <div
                    key={video.url}
                    className={`${mediaThumbCls} ${on ? "border-blue-400" : "border-white/15"}`}
                  >
                    <button
                      type="button"
                      aria-pressed={on}
                      aria-label="Toggle uploaded video"
                      onClick={() => toggleUploadVideo(video.url)}
                      className="w-full h-full"
                    >
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="flex items-center justify-center h-full bg-black/50 text-[11px] font-['Manrope'] text-white">Video</span>
                      )}
                    </button>
                    <RemoveThumbButton label="Remove uploaded video" onClick={() => removeUpload(video.url)} />
                  </div>
                );
              })}
              {pickedPhotos.map((photo) => (
                <div
                  key={`${photo.vehicleId}-${photo.url}`}
                  title={photo.vehicleLabel || "Vehicle"}
                  className={`${mediaThumbCls} border-blue-400`}
                >
                  <img src={photo.thumbnail_url || photo.url} alt="" className="w-full h-full object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-black/75 px-1 py-0.5 text-[10px] font-['Manrope'] text-white truncate">
                    {photo.vehicleLabel || "Vehicle"}
                  </span>
                  <RemoveThumbButton label="Remove inventory photo" onClick={() => removeVehiclePick(photo.url)} />
                </div>
              ))}
              {pickedVideos.map((video) => (
                <div
                  key={`${video.vehicleId}-${video.url}`}
                  title={video.vehicleLabel || "Vehicle"}
                  className={`${mediaThumbCls} ${allowVideos ? "border-blue-400" : "border-white/15"}`}
                >
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="flex items-center justify-center h-full bg-black/50 text-[11px] font-['Manrope'] text-white">Video</span>
                  )}
                  <span className="absolute bottom-0 inset-x-0 bg-black/75 px-1 py-0.5 text-[10px] font-['Manrope'] text-white truncate">
                    {video.vehicleLabel || "Vehicle"}
                  </span>
                  <RemoveThumbButton label="Remove inventory video" onClick={() => removeVehiclePick(video.url)} />
                </div>
              ))}
              <input
                ref={uploadRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.mov,.m4v"
                multiple
                className="hidden"
                data-testid="imagine-upload-input"
                onChange={(event) => handleUpload(event.target.files)}
              />
              <button
                type="button"
                data-testid="imagine-upload"
                disabled={uploading || !configured}
                onClick={() => uploadRef.current?.click()}
                className={`${mediaTileCls} border-dashed border-white/25`}
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                <span className="text-[11px] font-['Manrope'] leading-tight px-1">Upload</span>
              </button>
              <button
                type="button"
                data-testid="imagine-pick-vehicle"
                onClick={openVehiclePicker}
                className={`${mediaTileCls} border-dashed border-white/25`}
              >
                <Car className="w-5 h-5" />
                <span className="text-[11px] font-['Manrope'] leading-tight px-1 text-center">Vehicle</span>
              </button>
            </div>
            <p className="mt-2 text-xs font-['Manrope'] text-white/40">
              {referenceLimitHint(activeGen, mode)}
              {allowPhotos ? ` ${chosenImages.length}/${maxImages} photos` : ""}
              {allowVideos ? ` · ${chosenVideos.length}/${maxVideos} videos` : ""}
            </p>
            {mode !== "video" && (uploadVideos.length > 0 || pickedVideos.length > 0) && (
              <p className="mt-1 text-xs font-['Manrope'] text-white/40">
                Videos stay here. Switch to Video to include them.
              </p>
            )}
          </div>

          {supportsFrames && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["First frame", firstFrameUrl, setFirstFrameUrl, "imagine-first-frame"],
                ["Last frame", lastFrameUrl, setLastFrameUrl, "imagine-last-frame"],
              ].map(([label, value, setValue, testId]) => (
                <div key={label}>
                  <span className={labelCls}>{label}</span>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    <button
                      type="button"
                      data-testid={`${testId}-none`}
                      onClick={() => setValue("")}
                      className={`shrink-0 min-h-11 px-3 border font-['Manrope'] text-sm ${
                        value ? "border-white/15 text-white/60" : "border-blue-400 text-white"
                      }`}
                    >
                      None
                    </button>
                    {framePhotos.map((photo) => (
                      <button
                        key={`${testId}-${photo.url}`}
                        type="button"
                        aria-pressed={value === photo.url}
                        onClick={() => setValue(photo.url)}
                        className={`shrink-0 w-14 h-14 border overflow-hidden ${
                          value === photo.url ? "border-blue-400" : "border-white/15"
                        }`}
                      >
                        <img src={photo.thumbnail_url || photo.url} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(fields.includes("duration") || resolutionChoices(activeGen).length > 0 || aspectChoices(activeGen).length > 0 || qualityChoices(activeGen).length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              {fields.includes("duration") && (
                <div>
                  <label className={labelCls} htmlFor="imagine-duration">Duration (sec)</label>
                  <input
                    id="imagine-duration"
                    type="number"
                    min={1}
                    max={30}
                    className={inputCls}
                    value={duration}
                    onChange={(event) => setDuration(event.target.value)}
                  />
                </div>
              )}
              {resolutionChoices(activeGen).length > 0 && (
                <div>
                  <label className={labelCls} htmlFor="imagine-resolution">Resolution</label>
                  <select
                    id="imagine-resolution"
                    className={selectCls}
                    value={resolution}
                    onChange={(event) => setResolution(event.target.value)}
                  >
                    {resolutionChoices(activeGen).map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
              )}
              {aspectChoices(activeGen).length > 0 && (
                <div>
                  <label className={labelCls} htmlFor="imagine-aspect">Aspect ratio</label>
                  <select
                    id="imagine-aspect"
                    className={selectCls}
                    value={aspectRatio}
                    onChange={(event) => setAspectRatio(event.target.value)}
                  >
                    {aspectChoices(activeGen).map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
              )}
              {qualityChoices(activeGen).length > 0 && (
                <div>
                  <label className={labelCls} htmlFor="imagine-quality">Quality</label>
                  <select
                    id="imagine-quality"
                    className={selectCls}
                    value={quality}
                    onChange={(event) => setQuality(event.target.value)}
                  >
                    {qualityChoices(activeGen).map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {wantsAudio && (
            <label className="flex items-center gap-3 min-h-11 font-['Manrope'] text-sm text-white/80">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={Boolean(generateAudio)}
                onChange={(event) => setGenerateAudio(event.target.checked)}
              />
              Generate audio
            </label>
          )}

          <div className="hidden lg:sticky lg:bottom-0 lg:block bg-[#111111] pt-3">
            {generateButton("imagine-generate", "w-full")}
          </div>
          {generateWorking && (
            <p className="text-xs font-['Manrope'] text-white/50" role="status">
              Higgsfield is generating. A preview will appear in Results when it finishes.
            </p>
          )}
        </section>

        <section className="space-y-3" data-testid="imagine-results">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-xs font-['Unbounded'] uppercase tracking-wide text-white/55">Results</h2>
            {jobs.length > 0 && (
              <select
                className={`${selectCls} w-auto min-h-11`}
                aria-label="Results per page"
                value={resultsPageSize}
                onChange={(event) => {
                  setResultsPageSize(Number(event.target.value));
                  setResultsPage(1);
                }}
              >
                {RESULTS_PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </select>
            )}
          </div>
          {!jobs.length && (
            <p className="text-sm font-['Manrope'] text-white/40">No marketing generations yet.</p>
          )}
          {pagedJobs.map((job) => {
            const media = mediaItems(job);
            const working = isWorkingJob(job);
            const timedOut = isTimeoutJob(job);
            return (
              <article key={job.id} className="border border-white/10 bg-black/20 p-3 space-y-2" aria-busy={working}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-['Manrope'] text-white">
                    {timedOut && !media.length ? "Checking Higgsfield" : statusLabel(job.status)}
                    <span className="text-white/45"> · {jobKindLabel(job)} · {job.model_id}</span>
                  </p>
                  {job.in_media && (
                    <span className="text-xs font-['Manrope'] text-emerald-300">On the vehicle</span>
                  )}
                </div>
                {job.vehicle_label && (
                  <p className="text-xs font-['Manrope'] text-white/55">
                    {job.vehicle_label}{job.vehicle_stock ? ` · Stock ${job.vehicle_stock}` : ""}
                  </p>
                )}
                {job.prompt && (
                  <p className="text-xs font-['Manrope'] text-white/55 whitespace-pre-wrap">{job.prompt}</p>
                )}
                {timedOut && (
                  <p className="text-sm font-['Manrope'] text-amber-300">
                    Still checking Higgsfield for a finished result.
                  </p>
                )}
                {job.error && !timedOut && (
                  <p className="text-sm font-['Manrope'] text-red-400">{job.error}</p>
                )}
                {working && !media.length && (
                  <div className="flex flex-col items-center justify-center gap-3 w-full min-h-[180px] bg-black/50 border border-white/10" role="status">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                    <p className="text-sm font-['Manrope'] text-white/80">{workingLabel(job)}</p>
                  </div>
                )}
                {media.map((item) => (
                  isVideoMedia(item) ? (
                    <video
                      key={item.url}
                      src={item.url}
                      controls
                      playsInline
                      preload="metadata"
                      poster={item.thumbnail_url || undefined}
                      className="w-full bg-black"
                    />
                  ) : (
                    <img key={item.url} src={item.url} alt="Generated Imagine result" className="w-full object-contain bg-black/40" />
                  )
                ))}
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setInfoJob(job)}
                    className="min-h-11 px-4 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
                  >
                    View more info
                  </button>
                  {media.length > 0 && (
                    <button
                      type="button"
                      disabled={downloadingId === job.id}
                      onClick={() => downloadJob(job)}
                      className="min-h-11 px-4 inline-flex items-center justify-center gap-2 border border-white/15 text-white font-['Manrope'] hover:bg-white/5 disabled:opacity-50"
                    >
                      {downloadingId === job.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {downloadingId === job.id ? "Downloading…" : "Download"}
                    </button>
                  )}
                  {job.vehicle_id && job.status === "completed" && media.length > 0 && (
                    <button
                      type="button"
                      disabled={applyingId === job.id}
                      onClick={() => applyJob(job)}
                      className={`min-h-11 px-4 font-['Manrope'] font-semibold disabled:opacity-50 ${
                        job.in_media
                          ? "border border-white/15 text-white hover:bg-white/5"
                          : "bg-blue-500 hover:bg-blue-600 text-white"
                      }`}
                    >
                      {applyingId === job.id
                        ? (job.in_media ? "Removing…" : "Adding…")
                        : job.in_media
                          ? "Remove from vehicle"
                          : "Add to vehicle"}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
          {jobs.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <p className="text-sm font-['Manrope'] text-white/50">
                Showing {pagedJobs.length} of {jobs.length} · Page {resultsSafePage} of {resultsTotalPages}
              </p>
              {resultsTotalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={resultsSafePage <= 1}
                    onClick={() => setResultsPage((page) => Math.max(1, page - 1))}
                    className="min-h-11 px-3 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={resultsSafePage >= resultsTotalPages}
                    onClick={() => setResultsPage((page) => Math.min(resultsTotalPages, page + 1))}
                    className="min-h-11 px-3 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#050505]/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {generateButton("imagine-generate-mobile", "w-full")}
      </div>

      <AdminDialog
        open={vehicleOpen}
        onClose={() => setVehicleOpen(false)}
        title={pickerVehicle ? vehicleTitle(pickerVehicle) || "Vehicle media" : "Select a vehicle"}
        maxWidth="max-w-3xl"
        testId="imagine-vehicle-picker"
        footer={pickerVehicle ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setPickerVehicle(null)}
              className="min-h-11 px-4 border border-white/15 text-white/80 font-['Manrope']"
            >
              All vehicles
            </button>
            <button
              type="button"
              data-testid="imagine-use-vehicle-media"
              onClick={confirmVehicleMedia}
              className="min-h-11 px-4 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold"
            >
              Use selected
            </button>
          </div>
        ) : null}
      >
        {!pickerVehicle && (
          <div className="space-y-3">
            <input
              type="search"
              value={vehicleQuery}
              onChange={(event) => setVehicleQuery(event.target.value)}
              placeholder="Search year, make, model, or stock"
              aria-label="Search vehicles"
              className={inputCls}
              data-testid="imagine-vehicle-search"
            />
            {vehiclesLoading && (
              <p className="text-sm font-['Manrope'] text-white/50 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading vehicles
              </p>
            )}
            {!vehiclesLoading && !filteredVehicles.length && (
              <p className="text-sm font-['Manrope'] text-white/40">No vehicles match that search.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filteredVehicles.map((row) => {
                const thumb = row.photos?.[0]?.url;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => openVehicleMedia(row)}
                    className="flex items-center gap-3 min-h-16 p-2 text-left border border-white/10 hover:border-blue-400 bg-black/20"
                  >
                    {thumb ? (
                      <img src={thumb} alt="" className="w-16 h-12 object-cover shrink-0 bg-black" />
                    ) : (
                      <span className="w-16 h-12 shrink-0 bg-white/5" />
                    )}
                    <span className="min-w-0">
                      <span className="block text-sm font-['Manrope'] text-white truncate">{vehicleTitle(row) || "Vehicle"}</span>
                      <span className="block text-xs font-['Manrope'] text-white/45 truncate">
                        {row.stock_number ? `Stock ${row.stock_number}` : "No stock number"}
                        {row.status === "sold" ? " · Sold" : ""}
                        {vehiclePicks.some((item) => item.vehicleId === row.id) ? " · In use" : ""}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {pickerVehicle && (
          <div className="space-y-4">
            <p className="text-sm font-['Manrope'] text-white/55">
              Check the inventory photos{allowVideos ? " and videos" : ""} you want. Use selected adds only those. Media already chosen from other vehicles stays selected.
              {allowPhotos ? ` ${pickerImages.length + selectedImages.length + pickedPhotos.filter((item) => item.vehicleId !== pickerVehicle.id).length}/${maxImages} photos` : ""}
              {allowVideos ? ` · ${pickerVideos.length + selectedVideos.length + pickedVideos.filter((item) => item.vehicleId !== pickerVehicle.id).length}/${maxVideos} videos` : ""}
            </p>
            <div className="flex flex-wrap gap-3">
              {(pickerVehicle.photos || []).map((photo) => {
                const on = pickerImages.includes(photo.url);
                return (
                  <button
                    key={photo.url}
                    type="button"
                    aria-pressed={on}
                    disabled={!allowPhotos}
                    onClick={() => togglePickerPhoto(photo.url)}
                    className={`${mediaThumbCls} ${on ? "border-blue-400" : "border-white/15"} disabled:opacity-40`}
                  >
                    <img src={photo.thumbnail_url || photo.url} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
              {(pickerVehicle.videos || []).map((video) => {
                const on = pickerVideos.includes(video.url);
                return (
                  <button
                    key={video.url}
                    type="button"
                    aria-pressed={on}
                    disabled={!allowVideos}
                    onClick={() => togglePickerVideo(video.url)}
                    className={`${mediaThumbCls} ${on ? "border-blue-400" : "border-white/15"} disabled:opacity-40`}
                  >
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="flex items-center justify-center h-full bg-black/50 text-[11px] font-['Manrope'] text-white">Video</span>
                    )}
                  </button>
                );
              })}
            </div>
            {!pickerVehicle.photos?.length && !pickerVehicle.videos?.length && (
              <p className="text-sm font-['Manrope'] text-white/40">This vehicle has no inventory media yet.</p>
            )}
          </div>
        )}
      </AdminDialog>

      <AdminDialog
        open={Boolean(infoJob)}
        onClose={() => setInfoJob(null)}
        title="Generation info"
        maxWidth="max-w-lg"
        testId="imagine-job-info"
      >
        {infoJob && (
          <dl className="space-y-4 font-['Manrope']">
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Approximate cost</dt>
              <dd className="mt-1 text-sm text-white">{formatJobCost(infoJob)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Generated on</dt>
              <dd className="mt-1 text-sm text-white">{formatDateTimeMdY(infoJob.created_at)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">Generated by</dt>
              <dd className="mt-1 text-sm text-white">
                <span className="block">{infoGenerated.name}</span>
                {infoGenerated.role ? <span className="block text-white/60 mt-0.5">{infoGenerated.role}</span> : null}
              </dd>
            </div>
            {infoJob.vehicle_label && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-white/50">Vehicle</dt>
                <dd className="mt-1 text-sm text-white">
                  {infoJob.vehicle_label}{infoJob.vehicle_stock ? ` · Stock ${infoJob.vehicle_stock}` : ""}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs uppercase tracking-wide text-white/50">References</dt>
              <dd className="mt-2">
                {infoRefs.length ? (
                  <div className="flex flex-wrap gap-2">
                    {infoRefs.map((url) => (
                      isVideoMedia({ url }) ? (
                        <video key={url} src={url} muted playsInline preload="metadata" className="w-20 h-20 object-cover bg-black border border-white/10" />
                      ) : (
                        <img key={url} src={url} alt="" className="w-20 h-20 object-cover bg-black/40 border border-white/10" />
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-white/40">No reference images.</p>
                )}
              </dd>
            </div>
          </dl>
        )}
      </AdminDialog>
    </div>
  );
}
